import React, { useRef, useState } from 'react';
import { Dialog, Tab } from '@fx-ui/fine-design';
import { useVerificationGuard } from '@fx-ui/platform-sdk';
import AddMemberPanel from './AddMemberPanel';
import PublicLinkPanel from './PublicLinkPanel';
import { IDept, MEMBER_INVITE_FLAG, ResignedMemberRecoveryInfo } from '~/interfaces';
import {
    first,
    forEach,
    hasFunctionExemptTrialImport,
    isEmpty,
    localStorageManager,
    StorageKeyManager,
    trackVerifyEntry,
    tracker
} from '~/utils';
import Intl from '@fx-ui/intl';
import { TRACKER, helpSourceIntl } from '~/constants';
import { useStores } from '~/hooks';
import { ExemptTrialImportPanel, ImportMemberPanel } from './components';
import { MemberFieldProvider } from '~/providers';

interface IMemberInviteDialogProps {
    visible: boolean;
    currentDeptInfo?: IDept;
    disabledDeptIds?: string[];
    resignedMember?: ResignedMemberRecoveryInfo;
    subTitle?: string;
    onClose: (changeInfo?: { isMemberChange?: boolean; refreshDept?: boolean }) => void;
}

interface MemberInvitePanelRef {
    invite: () => Promise<void> | void;
    clear: () => void;
}

const INVITE_TABS = window.jdy_config.member_invite.tabs;
type TabKey = (typeof INVITE_TABS)[number];
interface TabItem {
    tab: string;
    key: TabKey;
    content: React.ReactNode;
}

type GuardHandle = ReturnType<typeof useVerificationGuard>;

const GuardRenderer: React.FC<{
    children: (verification: GuardHandle) => React.ReactNode;
}> = ({ children }) => {
    const verification = useVerificationGuard({ helpUrl: helpSourceIntl.get('doc_share_config') });
    return <>{children(verification)}</>;
};
/**
 * 通讯录-邀请成员
 * @param props 组件属性
 */
const MemberInviteDialog: React.FC<IMemberInviteDialogProps> = (props) => {
    const { visible, currentDeptInfo, disabledDeptIds, resignedMember, subTitle, onClose } = props;
    const { corpUserStore } = useStores();
    // 是否为离职成员恢复在职场景
    const isRecoverResignedMember = !isEmpty(resignedMember);

    const TabItem = Tab.Item;

    const thisRef = useRef({
        isMemberChange: false,
        refreshDept: false
    });

    const renderWithGuard = (
        verification: GuardHandle | undefined,
        content: React.ReactNode,
        sceneKey: string
    ) => {
        const dialogContent = <MemberFieldProvider>{content}</MemberFieldProvider>;

        if (!verification) {
            return dialogContent;
        }

        return verification.renderGuard(dialogContent, {
            onEntryClick: (type) => trackVerifyEntry(type, corpUserStore, sceneKey)
        });
    };

    const getTabData = (verification?: GuardHandle) => {
        const tabData: TabItem[] = [];
        forEach(INVITE_TABS, (key) => {
            switch (key) {
                case 'add':
                    tabData.push({
                        tab: Intl.get('member.invite.manual'),
                        key: 'add',
                        content: renderWithGuard(
                            verification,
                            renderInviteMember(),
                            'platform_member_invite_manual'
                        )
                    });
                    break;
                case 'import':
                    tabData.push({
                        tab: Intl.get('member.invite.import'),
                        key: 'import',
                        content: renderWithGuard(
                            verification,
                            renderImportMember(),
                            'platform_member_invite_import'
                        )
                    });
                    break;
                case 'link':
                    tabData.push({
                        tab: Intl.get('member.invite.public.link'),
                        key: 'link',
                        content: renderPublicLink()
                    });
                    break;
                default:
                    break;
            }
        });
        return tabData;
    };

    const [activeTab, setActiveTab] = useState<string | undefined>(first(INVITE_TABS));
    const isAddTab = activeTab === 'add' || isRecoverResignedMember;
    const addRef = useRef<MemberInvitePanelRef>(null);

    const handleInviteMember = () => {
        if (isRecoverResignedMember) {
            onClose({ isMemberChange: true });
            return;
        }
        thisRef.current.isMemberChange = true;
    };

    const renderInviteMember = () => {
        return (
            <AddMemberPanel
                ref={addRef}
                currentDeptInfo={currentDeptInfo}
                disabledDeptIds={disabledDeptIds}
                resignedMember={resignedMember}
                onInviteMember={handleInviteMember}
            />
        );
    };

    const renderImportMember = () => {
        const commonProps = {
            onImportSuccess: () => {
                thisRef.current.isMemberChange = true;
                thisRef.current.refreshDept = true;
            }
        };

        if (hasFunctionExemptTrialImport()) {
            return <ExemptTrialImportPanel {...commonProps} />;
        }

        return <ImportMemberPanel {...commonProps} />;
    };

    const renderPublicLink = () => {
        return <PublicLinkPanel currentDeptInfo={currentDeptInfo} />;
    };

    const onTabActive = (key: string) => {
        setActiveTab(key);
        if (key === 'import') {
            tracker(TRACKER.MEMBER_INVITE_MULTI);
        }
    };

    const renderDialogContent = (verification?: GuardHandle) => {
        if (isRecoverResignedMember) {
            return (
                <div className="member-invite-container">
                    {renderWithGuard(
                        verification,
                        renderInviteMember(),
                        'platform_member_reemployment_invite'
                    )}
                </div>
            );
        }

        return (
            <div className="member-invite-container">
                <Tab
                    className="member-invite-tab"
                    tabHeaderClassName="member-invite-header"
                    stretch
                    onTabActive={onTabActive}
                    size="small"
                    activeKey={activeTab}
                >
                    {getTabData(verification).map((item) => {
                        const title = item.tab;
                        return (
                            <TabItem tab={<span title={title}>{title}</span>} key={item.key}>
                                {item.content}
                            </TabItem>
                        );
                    })}
                </Tab>
            </div>
        );
    };

    const needVerification = corpUserStore.isPlatformFreeVersion;

    return (
        <Dialog
            className="fx-member-invite-dialog"
            visible={visible}
            onAfterClose={() => setActiveTab(first(INVITE_TABS))}
            title={Intl.get('member.invite')}
            subTitle={subTitle}
            separator={{ footer: isAddTab }}
            width={640}
            height={606}
            text4Ok={isAddTab ? Intl.get('invite') : null}
            props4Ok={
                isAddTab
                    ? {
                          type: 'primary',
                          loadingWhenClick: true,
                          onClick: () => addRef.current?.invite()
                      }
                    : undefined
            }
            text4Cancel={isAddTab ? Intl.get('clear') : null}
            props4Cancel={
                isAddTab
                    ? {
                          type: 'negative',
                          onClick: () => addRef.current?.clear()
                      }
                    : undefined
            }
            contentPadding={false}
            onCancel={() => {
                const isMemberChange = thisRef.current.isMemberChange;
                const inviteMemberKey = StorageKeyManager.getInviteMemberKey({
                    userId: corpUserStore.userId
                });
                const inviteMemberCache = localStorageManager.get(inviteMemberKey);
                if (isMemberChange && inviteMemberCache !== MEMBER_INVITE_FLAG.DONE) {
                    localStorageManager.set(inviteMemberKey, MEMBER_INVITE_FLAG.DONE, 365);
                }
                onClose({
                    isMemberChange,
                    refreshDept: thisRef.current.refreshDept
                });
                thisRef.current.isMemberChange = false;
                thisRef.current.refreshDept = false;
            }}
        >
            {/* 避免隐藏状态下父组件 remount 触发重新认证校验 */}
            {visible &&
                (needVerification ? (
                    <GuardRenderer>{renderDialogContent}</GuardRenderer>
                ) : (
                    renderDialogContent()
                ))}
        </Dialog>
    );
};

export default MemberInviteDialog;
